import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const JobCategoryContext = createContext();
 
export const useJobCategories = () => {
  const context = useContext(JobCategoryContext);
  if (!context) {
    throw new Error('useJobCategories must be used within a JobCategoryProvider');
  }
  return context;
};

export const JobCategoryProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [jobCategories, setJobCategories] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/job-categories');

  const getJobCategories = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      setPaginate(true)
      let response = await apiInstance.call('job-categories?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          if(filters?.active === true){
            query += 'active=1';
          }else{
            query += 'active=0';
          }
         
        }
        response = await apiInstance.call('job-categories?'+query);  
      }
      
      if (response.status === 200) {
       // setPaginate(response.data.paginate)
        setJobCategories(response.data.jobCategories || []);
       
        
        if(filters){
          return response.data.jobCategories;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch job categories information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, getToken,paginate]);

  const getJobCategoryByIdApi = useCallback(async (id, filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`job-categories/${id}/edit`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let jobCategories = {'data':[response.data.jobCategory]}
      setJobCategories(jobCategories || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/job-categories/edit/'+id))){
        if (getToken()) {
        
          
          await getJobCategoryByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getJobCategoryByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getJobCategories(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getJobCategories, searchParams, initialized,hideLoading,setApi]);

  const getJobCategoryAdditionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-job-categories-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get job category data'
      });
    } 
  }, [getToken, handleApiError]);


  const addJobCategory = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(data));

      const response = await apiInstance.call('job-categories', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the job categories list
        await getJobCategories();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Job Category'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create job category'
      });
      return { status: 500, error: error.message };
    }
  };
  
  const getJobCategoryById = useCallback(async (id) => {
     
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`job-categories/${id}/edit`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.jobCategory };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch job category details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch job category details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateJobCategory = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(updatedData));
      
      // Now send the request with formData only once
      const response = await apiInstance.call(`job-categories/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the job categories list
        await getJobCategories();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Job Category'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update job category'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteJobCategory = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`job-categories/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the job categories list
        await getJobCategories();
        navigate(getRoutePath('/job-categories'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete job category'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete job category'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <JobCategoryContext.Provider
      value={{
        jobCategories,
        addJobCategory,
        getJobCategoryById,
        updateJobCategory,
        deleteJobCategory,
        getJobCategories,
        getJobCategoryAdditionalData,
        formErrors,
        clearErrors,
        setPaginate,
        paginate
      }}
    >
      {children}
    </JobCategoryContext.Provider>
  );
};

export default JobCategoryContext; 